import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiCheckCircle, FiXCircle, FiArrowRight, FiAlertCircle, FiAlertTriangle, FiGlobe, FiClock, FiUser } from 'react-icons/fi';
import { datosAPI, flujoAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import ModalEnviarFlujo from '../components/ModalEnviarFlujo';

const RevisarDato = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { usuario, hasRole } = useAuth();
  const [dato, setDato] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [accion, setAccion] = useState('');
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    cargarDato();
  }, [id]);

  const cargarDato = async () => {
    try {
      setLoading(true);
      const response = await datosAPI.getPorId(id);
      setDato(response.data.data);
    } catch (error) {
      console.error('Error al cargar dato:', error);
      alert('Error al cargar los datos');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const getRolActual = () => {
    if (hasRole('gestor')) return 'gestor';
    if (hasRole('validador')) return 'validador';
    if (hasRole('editor')) return 'editor';
    return null;
  };

  const getRutaEditar = () => {
    const rol = getRolActual();
    return `/historicidad/${rol}/editar/${id}`;
  };

  const getRutaBandeja = () => {
    const rol = getRolActual();
    return `/historicidad/${rol}`;
  };

  const abrirModal = (tipoAccion) => {
    setAccion(tipoAccion);
    setMotivo('');
    setModalOpen(true);
  };

  const cerrarModal = () => {
    setModalOpen(false);
    setAccion('');
    setMotivo('');
  };

  const confirmarAccion = async () => {
    if (!dato) return;

    try {
      let estadoDestino = '';
      let motivoTexto = motivo;

      // Determinar estado destino según la acción y el rol
      const rol = getRolActual();
      
      switch (accion) {
        case 'aprobar':
          if (rol === 'validador') {
            estadoDestino = 'informacion_contrastada_editor';
            motivoTexto = motivoTexto || 'Información validada. Lista para revisión editorial.';
          } else if (rol === 'editor') {
            estadoDestino = 'informacion_aprobada';
            motivoTexto = motivoTexto || 'Información aprobada y lista para publicación.';
          }
          break;
        case 'devolver':
          if (rol === 'validador') {
            estadoDestino = 'creada_gestor';
          } else if (rol === 'editor') {
            estadoDestino = 'informacion_valida';
          }
          if (!motivoTexto.trim()) {
            alert('Debes proporcionar un motivo para devolver');
            return;
          }
          break;
        case 'pendiente':
          estadoDestino = 'pendiente';
          if (!motivoTexto.trim()) {
            alert('Debes proporcionar un motivo para marcar como pendiente');
            return;
          }
          break;
        case 'rechazar':
          estadoDestino = 'rechazada';
          if (!motivoTexto.trim()) {
            alert('Debes proporcionar un motivo detallado para rechazar (ej: plagio, información falsa, etc.)');
            return;
          }
          break;
      }

      await flujoAPI.transicionar(dato.id, estadoDestino, motivoTexto);
      
      alert('Acción completada exitosamente');
      navigate(getRutaBandeja());
    } catch (error) {
      console.error('Error al procesar acción:', error);
      alert(error.response?.data?.message || 'Error al procesar la acción');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!dato) return null;

  const rolActual = getRolActual();
  const esValidador = hasRole('validador');
  const esEditor = hasRole('editor');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className={`text-white ${
        esValidador ? 'bg-gradient-to-r from-purple-600 to-indigo-600' :
        esEditor ? 'bg-gradient-to-r from-green-600 to-teal-600' :
        'bg-gradient-to-r from-blue-600 to-cyan-600'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(getRutaBandeja())}
                className="p-2 hover:bg-white/20 rounded-lg transition"
              >
                <FiArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-2xl font-bold">Revisión de Dato Histórico</h1>
                <p className="text-sm opacity-90">ID: {dato.id} | Slug: {dato.slug}</p>
              </div>
            </div>
            <Link
              to={getRutaEditar()}
              className="flex items-center space-x-2 px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition font-medium"
            >
              <FiEdit />
              <span>Editar</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contenido principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Información básica */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{dato.tema}</h2>
              
              {/* Imagen */}
              {dato.imagen && (
                <img
                  src={dato.imagen}
                  alt={dato.tema}
                  className="w-full h-64 object-cover rounded-lg mb-4"
                />
              )}

              {/* Catálogos */}
              <div className="flex flex-wrap gap-2 mb-4">
                {dato.catalogos?.map((cat) => (
                  <span
                    key={cat.id}
                    className="px-3 py-1 text-sm rounded-full font-medium"
                    style={{
                      backgroundColor: `${cat.color}20`,
                      color: cat.color
                    }}
                  >
                    {cat.nombre}
                  </span>
                ))}
              </div>

              {/* Descripción */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Descripción</h3>
                <p className="text-gray-700">{dato.descripcion}</p>
              </div>

              {/* Texto histórico */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Texto Histórico</h3>
                <div
                  className="prose max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: dato.textoHistorico }}
                />
              </div>

              {/* Datos adicionales en grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {dato.citaBiblica && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-1">Cita Bíblica</h4>
                    <p className="text-gray-900">{dato.citaBiblica}</p>
                  </div>
                )}
                {dato.fechaEvento && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-1">Fecha del Evento</h4>
                    <p className="text-gray-900">{new Date(dato.fechaEvento).toLocaleDateString()}</p>
                  </div>
                )}
                {dato.strongHebreo && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-1">Strong Hebreo</h4>
                    <p className="text-gray-900">{dato.strongHebreo}</p>
                  </div>
                )}
                {dato.strongGriego && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-1">Strong Griego</h4>
                    <p className="text-gray-900">{dato.strongGriego}</p>
                  </div>
                )}
              </div>

              {dato.analisisEncontrado && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-600 mb-1">Análisis Encontrado</h4>
                  <p className="text-gray-700">{dato.analisisEncontrado}</p>
                </div>
              )}

              {dato.hechosHistoricos && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-600 mb-1">Hechos Históricos Relacionados</h4>
                  <p className="text-gray-700">{dato.hechosHistoricos}</p>
                </div>
              )}

              {dato.referencias && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-600 mb-1">Referencias</h4>
                  <p className="text-gray-700">{dato.referencias}</p>
                </div>
              )}

              {dato.palabrasClave && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-600 mb-1">Palabras Clave</h4>
                  <p className="text-gray-700">{dato.palabrasClave}</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar con métricas y acciones */}
          <div className="space-y-6">
            {/* Porcentajes */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Evaluación</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-600">Historicidad</span>
                    <span className="text-sm font-bold text-blue-600">{dato.porcentajeHistoricidad?.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${dato.porcentajeHistoricidad}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-600">Autenticidad</span>
                    <span className="text-sm font-bold text-green-600">{dato.porcentajeAutenticidad?.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${dato.porcentajeAutenticidad}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-600">Veracidad</span>
                    <span className="text-sm font-bold text-purple-600">{dato.porcentajeVeracidad?.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-purple-600 h-2 rounded-full"
                      style={{ width: `${dato.porcentajeVeracidad}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Información de gestión */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Gestión</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Estado</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    dato.estado === 'informacion_aprobada' ? 'bg-green-100 text-green-800' :
                    dato.estado === 'informacion_contrastada_editor' ? 'bg-blue-100 text-blue-800' :
                    dato.estado === 'informacion_valida' ? 'bg-purple-100 text-purple-800' :
                    dato.estado === 'creada_gestor' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {dato.estado}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Prioridad</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    dato.prioridad === 'alta' ? 'bg-red-100 text-red-800' :
                    dato.prioridad === 'media' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {dato.prioridad}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Criticidad</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    dato.criticidad === 'alta' ? 'bg-red-100 text-red-800' :
                    dato.criticidad === 'media' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {dato.criticidad}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 flex items-center">
                    <FiClock className="mr-1" /> Investigación
                  </span>
                  <span className="font-medium text-gray-900">{dato.tiempoInvestigacionHoras || 0} hrs</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 flex items-center">
                    <FiUser className="mr-1" /> Visualizaciones
                  </span>
                  <span className="font-medium text-gray-900">{dato.visualizaciones || 0}</span>
                </div>
              </div>
            </div>

            {/* Acciones de flujo */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones</h3>
              <div className="space-y-2">
                {/* Acciones del Validador */}
                {esValidador && (
                  <>
                    <button
                      onClick={() => abrirModal('aprobar')}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium flex items-center justify-center"
                    >
                      <FiCheckCircle className="mr-2" /> Aprobar y Enviar a Editor
                    </button>
                    <button
                      onClick={() => abrirModal('devolver')}
                      className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition font-medium flex items-center justify-center"
                    >
                      <FiArrowLeft className="mr-2" /> Devolver a Gestor
                    </button>
                    <button
                      onClick={() => abrirModal('pendiente')}
                      className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition font-medium flex items-center justify-center"
                    >
                      <FiAlertCircle className="mr-2" /> Marcar como Pendiente
                    </button>
                    <button
                      onClick={() => abrirModal('rechazar')}
                      className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium flex items-center justify-center"
                    >
                      <FiXCircle className="mr-2" /> Rechazar
                    </button>
                  </>
                )}

                {/* Acciones del Editor */}
                {esEditor && (
                  <>
                    <button
                      onClick={() => abrirModal('aprobar')}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium flex items-center justify-center"
                    >
                      <FiGlobe className="mr-2" /> Aprobar y Publicar
                    </button>
                    <button
                      onClick={() => abrirModal('devolver')}
                      className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition font-medium flex items-center justify-center"
                    >
                      <FiArrowLeft className="mr-2" /> Devolver a Validador
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Retroalimentaciones y Observaciones */}
            {dato.retroalimentaciones && dato.retroalimentaciones.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FiAlertTriangle className="mr-2 text-orange-500" />
                  Retroalimentaciones y Observaciones
                </h3>
                <div className="space-y-3">
                  {dato.retroalimentaciones.map((retro, index) => (
                    <div 
                      key={index} 
                      className={`border-l-4 pl-3 py-2 rounded ${
                        retro.prioridad === 'alta' ? 'border-red-500 bg-red-50' :
                        retro.prioridad === 'media' ? 'border-yellow-500 bg-yellow-50' :
                        'border-blue-500 bg-blue-50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <p className="font-medium text-gray-900 text-sm">
                          {retro.tipo === 'general' ? 'Comentario General' : `Campo: ${retro.campo}`}
                        </p>
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          retro.resuelta ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                        }`}>
                          {retro.resuelta ? 'Resuelta' : 'Pendiente'}
                        </span>
                      </div>
                      <p className="text-gray-700 text-sm mb-2">{retro.mensaje}</p>
                      {retro.accionSugerida && (
                        <p className="text-gray-600 text-xs italic">
                          <strong>Acción sugerida:</strong> {retro.accionSugerida}
                        </p>
                      )}
                      <p className="text-gray-500 text-xs mt-1">
                        {new Date(retro.createdAt).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Historial de Transiciones */}
            {dato.transiciones && dato.transiciones.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Historial de Flujo</h3>
                <div className="space-y-2 text-sm">
                  {dato.transiciones.map((trans, index) => (
                    <div key={index} className="border-l-2 border-green-500 pl-3 py-2">
                      <p className="font-medium text-gray-900">
                        {trans.estadoOrigen} → {trans.estadoDestino}
                      </p>
                      {trans.motivoTransicion && (
                        <p className="text-gray-700 text-sm mt-1">{trans.motivoTransicion}</p>
                      )}
                      <p className="text-gray-500 text-xs mt-1">
                        {trans.rolOrigen} → {trans.rolDestino || 'Mismo rol'}
                        {' • '}
                        {new Date(trans.createdAt).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Historial de Cambios (si existe) */}
            {dato.historialCambios && dato.historialCambios.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Historial de Modificaciones</h3>
                <div className="space-y-2 text-sm">
                  {dato.historialCambios.slice(0, 5).map((item, index) => (
                    <div key={index} className="border-l-2 border-blue-500 pl-3 py-1">
                      <p className="font-medium text-gray-900">{item.accion}</p>
                      <p className="text-gray-600 text-xs">
                        {new Date(item.createdAt).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de confirmación */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">
              {accion === 'aprobar' && (esEditor ? 'Aprobar y Publicar' : 'Aprobar y enviar a Editor')}
              {accion === 'devolver' && (esEditor ? 'Devolver a Validador' : 'Devolver a Gestor')}
              {accion === 'pendiente' && 'Marcar como Pendiente'}
              {accion === 'rechazar' && 'Rechazar dato'}
            </h3>

            <p className="text-gray-600 mb-4">
              Dato: <strong>{dato.tema}</strong>
            </p>

            {accion === 'aprobar' && esEditor && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-green-800">
                  ⚠️ Esta acción hará que el dato sea visible públicamente en la plataforma.
                </p>
              </div>
            )}

            {accion === 'pendiente' && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-orange-800">
                  📋 El dato quedará en tu bandeja esperando información adicional o cambios.
                </p>
              </div>
            )}

            {accion === 'rechazar' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-800">
                  ⚠️ El dato quedará marcado como RECHAZADO y NO será visible públicamente.
                  <br />
                  <strong>Especifica el motivo:</strong> plagio, información falsa, falta de fuentes, etc.
                </p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {(accion === 'aprobar') ? 'Comentarios (opcionales):' : 'Motivo (requerido):'}
              </label>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={
                  accion === 'aprobar' ? 'Comentarios adicionales...' : 
                  accion === 'rechazar' ? 'Ej: Plagio de [fuente], Información falsa detectada en [sección], Falta de referencias confiables...' :
                  accion === 'pendiente' ? 'Ej: Esperando confirmación de fechas, Requiere más referencias, Verificar datos con fuente primaria...' :
                  'Explica el motivo de esta acción...'
                }
              />
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={cerrarModal}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarAccion}
                className={`px-4 py-2 rounded-lg text-white ${
                  accion === 'aprobar' ? 'bg-green-600 hover:bg-green-700' :
                  accion === 'devolver' ? 'bg-yellow-600 hover:bg-yellow-700' :
                  accion === 'pendiente' ? 'bg-orange-600 hover:bg-orange-700' :
                  'bg-red-600 hover:bg-red-700'
                }`}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevisarDato;

